import {
  DocumentActionType,
  DocumentsAction,
} from "pages/DevelopWorkspace/contexts/DocumentsContext/actions.types";
import { CANCEL_RESULT_PLACEHOLDER } from "pages/DevelopWorkspace/contexts/DocumentsContext/constants";
import getNextDocumentWithFocusTabs from "pages/DevelopWorkspace/contexts/DocumentsContext/helpers/getNextDocumentOutputTab";
import {
  isQueryStatementCompleted,
  isQueryStatementDispatched,
} from "pages/DevelopWorkspace/contexts/DocumentsContext/helpers/getQueryStatementState";
import { isQueryStatementChangesValid } from "pages/DevelopWorkspace/contexts/DocumentsContext/helpers/isQueryStatementChangesValid";
import { normalizeState } from "pages/DevelopWorkspace/contexts/DocumentsContext/helpers/normalizeState";
import { shouldIgnoreQSUpdate } from "pages/DevelopWorkspace/contexts/DocumentsContext/helpers/shouldIgnoreQSUpdate";
import {
  getDroppedGroupId,
  getGroupEndIndex,
  getNearestVisibleTabId,
  insertAt,
  snapOutOfGroup,
} from "pages/DevelopWorkspace/contexts/DocumentsContext/helpers/tabGroupOrdering";
import {
  CancellationStatus,
  DocumentAiStateInitialData,
  DocumentAiStateUpdate,
  DocumentEngineMonitoring,
  DocumentLayout,
  DocumentOutputTab,
  DocumentsState,
  Execution,
  ExecutionContext,
  QueryStatement,
  QueryStatementStatus,
  SortOrder,
  TabGroup,
  WorkspaceDocument,
} from "pages/DevelopWorkspace/workspace.types";

import {
  SwitchActiveOutputPayload,
  SwitchDocumentActiveQueryStatementPayload,
} from "./types";

// A tab moved into a collapsed group would be swallowed with no feedback
// Therefore we expand the group
const expandGroup = (tabGroups: TabGroup[], groupId: string | null | undefined) =>
  tabGroups.some(tabGroup => tabGroup.id === groupId && tabGroup.collapsed)
    ? tabGroups.map(tabGroup =>
        tabGroup.id === groupId ? { ...tabGroup, collapsed: false } : tabGroup
      )
    : tabGroups;

const actionHandlers = {
  [DocumentActionType.SORT_DOCUMENTS]: (
    state: DocumentsState,
    payload: SortOrder
  ): DocumentsState => {
    const sorted = [...state.documents].sort((a, b) => {
      return payload === SortOrder.NewestFirst
        ? b.createdAt - a.createdAt
        : a.createdAt - b.createdAt;
    });

    return {
      ...state,
      documents: sorted.flatMap(document => {
        if (!document.groupId) {
          return [document];
        }

        const run = sorted.filter(item => item.groupId === document.groupId);

        return run[0] === document ? run : [];
      }),
    };
  },
  [DocumentActionType.ADD_DOCUMENT]: (
    state: DocumentsState,
    payload: WorkspaceDocument
  ): DocumentsState => {
    return {
      ...state,
      // a new grouped tab is appended after the last tab in its group
      // while an ungrouped tab is appended to the end of the bar
      documents: insertAt(
        state.documents,
        payload.groupId
          ? getGroupEndIndex(
              state.documents,
              payload.groupId,
              state.documents.length
            )
          : state.documents.length,
        [payload]
      ),
      activeDocumentId: payload.id,
    };
  },
  [DocumentActionType.REMOVE_DOCUMENT]: (
    state: DocumentsState,
    payload: string
  ): DocumentsState => {
    const documentIdToRemove = payload;

    const documentToRemoveIndex = state.documents.findIndex(
      document => document.id === documentIdToRemove
    );

    const removingActiveDocument =
      documentIdToRemove === state.activeDocumentId;

    const nextDocuments = state.documents.filter(
      document => document.id !== documentIdToRemove
    );

    const getNewActiveDocumentId = () =>
      getNearestVisibleTabId(
        state.tabGroups,
        state.documents,
        documentToRemoveIndex
      ) ??
      nextDocuments[0]?.id ??
      null;

    return {
      ...state,
      documents: nextDocuments,
      activeDocumentId: removingActiveDocument
        ? getNewActiveDocumentId()
        : state.activeDocumentId,
    };
  },
  [DocumentActionType.REMOVE_ALL_DOCUMENTS]: (
    state: DocumentsState,
    payload: {
      exceptDocumentIds: string[];
    }
  ): DocumentsState => {
    const nextDocuments = state.documents.filter(document => {
      return payload.exceptDocumentIds.includes(document.id);
    });

    const getNextActiveDocumentId = () => {
      const activeDocumentId = state.activeDocumentId;
      const activeDocumentInNext = nextDocuments.find(
        document => document.id === activeDocumentId
      );

      if (activeDocumentInNext) {
        // keep the same active document
        return activeDocumentId;
      }

      return (
        getNearestVisibleTabId(state.tabGroups, nextDocuments) ??
        nextDocuments[0]?.id ??
        null
      );
    };

    return {
      ...state,
      documents: nextDocuments,
      activeDocumentId: getNextActiveDocumentId(),
    };
  },
  [DocumentActionType.SET_ACTIVE_DOCUMENT]: (
    state: DocumentsState,
    payload: string
  ): DocumentsState => {
    return {
      ...state,
      activeDocumentId: payload,
    };
  },
  [DocumentActionType.CHANGE_DOCUMENT_CONTEXT]: (
    state: DocumentsState,
    payload: {
      documentId: string;
      context: Partial<ExecutionContext>;
    }
  ): DocumentsState => {
    const { documentId, context } = payload;

    return {
      ...state,
      documents: state.documents.map(document => {
        if (document.id === documentId) {
          return {
            ...document,
            execution: document.execution
              ? {
                  ...document.execution,
                  // clear error when switching engine or database
                  documentExecutionError: null,
                }
              : null,
            context: {
              ...document.context,
              ...context,
            },
          };
        }

        return document;
      }),
    };
  },
  [DocumentActionType.INIT_DOCUMENT_EXECUTION]: (
    state: DocumentsState,
    payload: {
      documentId: string;
      execution: Execution;
    }
  ): DocumentsState => {
    const { documentId: executionDocumentId, execution } = payload;

    return {
      ...state,
      documents: state.documents.map(document => {
        if (document.id === executionDocumentId) {
          return {
            ...document,
            layout: {
              ...document.layout,
              activeOutputTab: DocumentOutputTab.Results,
            },
            execution,
          };
        }

        return document;
      }),
    };
  },
  [DocumentActionType.UPDATE_DOCUMENT_QUERY_STATEMENT]: (
    state: DocumentsState,
    payload: {
      documentId: string;
      id: string;
      queryStatement: Partial<Omit<QueryStatement, "prevStatus">>;
    }
  ): DocumentsState => {
    const {
      documentId: queryStatementDocumentId,
      id,
      queryStatement: nextQueryStatement,
    } = payload;

    return {
      ...state,
      documents: state.documents.map(document => {
        if (document.id === queryStatementDocumentId) {
          if (!document.execution) {
            return document;
          }

          const nextDocument = {
            ...document,
            execution: {
              ...document.execution,
              queryStatements: document.execution.queryStatements.map(
                queryStatement => {
                  if (queryStatement.id === id) {
                    if (
                      nextQueryStatement.status &&
                      shouldIgnoreQSUpdate(
                        queryStatement,
                        nextQueryStatement.status
                      )
                    ) {
                      return queryStatement;
                    }

                    if (
                      !isQueryStatementChangesValid(
                        queryStatement,
                        nextQueryStatement
                      )
                    ) {
                      return queryStatement;
                    }

                    const nextStatus = nextQueryStatement.status;
                    const currentStatus = queryStatement.status;

                    // Only update prevStatus if we're transitioning to a different non-null status
                    // and the new status is different from both current status and prevStatus
                    const shouldUpdatePrevStatus =
                      nextStatus &&
                      nextStatus !== currentStatus &&
                      nextStatus !== queryStatement.prevStatus;

                    const queryStatementState = {
                      ...queryStatement,
                      ...nextQueryStatement,
                      status: nextStatus || currentStatus,
                      prevStatus: shouldUpdatePrevStatus
                        ? currentStatus
                        : queryStatement.prevStatus,
                      // if new stats are empty, use the old ones (never erase the stats)
                      statistics: nextQueryStatement.statistics
                        ?.executionTimeSec
                        ? nextQueryStatement.statistics
                        : queryStatement.statistics,
                    };

                    return queryStatementState;
                  }

                  return queryStatement;
                }
              ),
            },
          };

          return getNextDocumentWithFocusTabs(nextDocument);
        }

        return document;
      }),
    };
  },
  [DocumentActionType.CANCEL_DOCUMENT_EXECUTION]: (
    state: DocumentsState,
    payload: string
  ): DocumentsState => {
    const documentIdToCancel = payload;

    return {
      ...state,
      documents: state.documents.map(document => {
        if (document.id === documentIdToCancel) {
          if (!document.execution) {
            return document;
          }

          const requireServerCancellation =
            document.context.engine.name !== "system";

          return {
            ...document,
            execution: {
              ...document.execution,
              cancellationStatus: requireServerCancellation
                ? CancellationStatus.Initiated
                : CancellationStatus.Cancelled,
              queryStatements: document.execution.queryStatements.map(
                queryStatement => {
                  if (
                    isQueryStatementDispatched(queryStatement) &&
                    requireServerCancellation
                  ) {
                    return queryStatement;
                  }

                  if (isQueryStatementCompleted(queryStatement)) {
                    return queryStatement;
                  }

                  return {
                    ...queryStatement,
                    result: {
                      ...CANCEL_RESULT_PLACEHOLDER,
                    },
                    status: QueryStatementStatus.cancelled,
                  };
                }
              ),
            },
          };
        }

        return document;
      }),
    };
  },
  [DocumentActionType.UPDATE_DOCUMENT_EXECUTION_CANCELLATION_STATUS]: (
    state: DocumentsState,
    payload: {
      documentId: string;
      queryStatementId: string;
      cancellationStatus: CancellationStatus;
    }
  ): DocumentsState => {
    const {
      documentId: cancellationStatusDocumentId,
      queryStatementId,
      cancellationStatus,
    } = payload;

    return {
      ...state,
      documents: state.documents.map(document => {
        if (document.id === cancellationStatusDocumentId) {
          if (!document.execution) {
            return document;
          }

          const queryStatement = document.execution.queryStatements.find(
            queryStatement => queryStatement.id === queryStatementId
          );

          if (!queryStatement) {
            // probably query statement belongs to previous execution session
            // don't update the execution object
            return document;
          }

          return {
            ...document,
            execution: {
              ...document.execution,
              cancellationStatus,
            },
          };
        }

        return document;
      }),
    };
  },

  [DocumentActionType.SWITCH_DOCUMENT_ACTIVE_QUERY_STATEMENT]: (
    state: DocumentsState,
    payload: SwitchDocumentActiveQueryStatementPayload
  ): DocumentsState => {
    const {
      documentId: activeQueryStatementDocumentId,
      queryStatementIndex,
      userSelectedActiveQueryStatementIndexTimestamp,
      activeOutputTab,
    } = payload;

    return {
      ...state,
      documents: state.documents.map(document => {
        if (document.id === activeQueryStatementDocumentId) {
          if (!document.execution) {
            return document;
          }

          return {
            ...document,
            execution: {
              ...document.execution,
              userSelectedActiveQueryStatementIndexTimestamp,
              activeQueryStatementIndex: queryStatementIndex,
            },
            layout: {
              ...document.layout,
              activeOutputTab,
            },
          };
        }

        return document;
      }),
    };
  },

  [DocumentActionType.SWITCH_ACTIVE_OUTPUT_TAB]: (
    state: DocumentsState,
    payload: SwitchActiveOutputPayload
  ): DocumentsState => {
    const { documentId: activeOutputTabDocumentId, activeOutputTab } = payload;

    return {
      ...state,
      documents: state.documents.map(document => {
        if (document.id === activeOutputTabDocumentId) {
          return {
            ...document,
            layout: {
              ...document.layout,
              activeOutputTab,
            },
          };
        }

        return document;
      }),
    };
  },

  [DocumentActionType.CHANGE_DOCUMENT_LAYOUT]: (
    state: DocumentsState,
    payload: {
      documentId: string;
      layout: Partial<DocumentLayout>;
    }
  ): DocumentsState => {
    const { documentId: layoutDocumentId, layout } = payload;

    return {
      ...state,
      documents: state.documents.map(document => {
        if (document.id === layoutDocumentId) {
          return {
            ...document,
            layout: {
              ...document.layout,
              ...layout,
            },
          };
        }

        return document;
      }),
    };
  },
  [DocumentActionType.SET_QUERY_STATEMENT_STATE]: (
    state: DocumentsState,
    payload: {
      documentId: string;
      queryStatementIndex: number;
      data: Record<string, unknown>;
    }
  ): DocumentsState => {
    const { documentId, queryStatementIndex, data } = payload;

    return {
      ...state,
      documents: state.documents.map(document => {
        if (document.id === documentId) {
          if (!document.execution) {
            return document;
          }
          return {
            ...document,
            execution: {
              ...document.execution,
              queryStatements: document.execution.queryStatements.map(
                (queryStatement, index) => {
                  if (index === queryStatementIndex) {
                    return {
                      ...queryStatement,
                      ...data,
                    };
                  }
                  return queryStatement;
                }
              ),
            },
          };
        }

        return document;
      }),
    };
  },
  [DocumentActionType.UPDATE_DOCUMENT_ENGINE_MONITORING]: (
    state: DocumentsState,
    payload: {
      documentId: string;
      engineMonitoring: DocumentEngineMonitoring;
    }
  ): DocumentsState => {
    const { documentId: engineMonitoringDocumentId, engineMonitoring } =
      payload;

    return {
      ...state,
      documents: state.documents.map(document => {
        if (document.id === engineMonitoringDocumentId) {
          return {
            ...document,
            engineMonitoring,
          };
        }

        return document;
      }),
    };
  },
  [DocumentActionType.REORDER_DOCUMENT]: (
    state: DocumentsState,
    payload: {
      documentId: string;
      overDocumentId: string;
    }
  ): DocumentsState => {
    const { documentId, overDocumentId } = payload;
    const overDocumentIndex = state.documents.findIndex(
      document => document.id === overDocumentId
    );
    const documentIndex = state.documents.findIndex(
      document => document.id === documentId
    );

    // Return unchanged state if either document doesn't exist
    if (documentIndex === -1 || overDocumentIndex === -1) {
      return state;
    }

    const document = state.documents[documentIndex];
    const rest = state.documents.filter(item => item.id !== documentId);

    return {
      ...state,
      documents: insertAt(rest, overDocumentIndex, [
        {
          ...document,
          groupId: getDroppedGroupId(
            document.groupId,
            rest[overDocumentIndex - 1]?.groupId ?? null,
            rest[overDocumentIndex]?.groupId ?? null
          ),
        },
      ]),
    };
  },
  [DocumentActionType.ADD_DOCUMENT_AI_STATE]: (
    state: DocumentsState,
    payload: {
      documentId: string;
      aiState: DocumentAiStateInitialData;
    }
  ): DocumentsState => {
    const { documentId, aiState } = payload;

    return {
      ...state,
      documents: state.documents.map(document => {
        if (document.id === documentId) {
          if (document.aiState) {
            console.warn(
              "Trying to add AI state for document with existing AI state"
            );
            return document;
          }

          return {
            ...document,
            aiState: {
              ...aiState,
              aiResponse: null,
              isLoading: false,
              lastError: null,
            },
          };
        }
        return document;
      }),
    };
  },
  [DocumentActionType.REMOVE_DOCUMENT_AI_STATE]: (
    state: DocumentsState,
    payload: {
      documentId: string;
    }
  ): DocumentsState => {
    const { documentId } = payload;

    return {
      ...state,
      documents: state.documents.map(document => {
        if (document.id === documentId) {
          return {
            ...document,
            aiState: null,
          };
        }
        return document;
      }),
    };
  },
  [DocumentActionType.UPDATE_DOCUMENT_AI_STATE]: (
    state: DocumentsState,
    payload: {
      documentId: string;
      newAiState: DocumentAiStateUpdate;
    }
  ): DocumentsState => {
    const { documentId, newAiState } = payload;

    // Early exit if target document does not exist or has no AI state
    // Probably the document was removed
    const target = state.documents.find(d => d.id === documentId);
    if (!target || !target.aiState) {
      return state;
    }

    return {
      ...state,
      documents: state.documents.map(document => {
        if (document.id === documentId) {
          if (!document.aiState) {
            return document;
          }

          return {
            ...document,
            aiState: {
              ...document.aiState,
              ...newAiState,
              autoRun: false, // Important: prevent repeated triggering
            },
          };
        }
        return document;
      }),
    };
  },
  [DocumentActionType.ADD_TAB_GROUP]: (
    state: DocumentsState,
    payload: {
      tabGroup: TabGroup;
      documentIds: string[];
    }
  ): DocumentsState => {
    const { tabGroup, documentIds } = payload;

    const members = state.documents
      .filter(document => documentIds.includes(document.id))
      .map(document => ({ ...document, groupId: tabGroup.id }));

    if (!members.length) {
      return state;
    }

    const rest = state.documents.filter(
      document => !documentIds.includes(document.id)
    );
    const firstIndex = state.documents.findIndex(document =>
      documentIds.includes(document.id)
    );

    return {
      ...state,
      tabGroups: [...state.tabGroups, tabGroup],
      documents: insertAt(
        rest,
        snapOutOfGroup(rest, Math.min(firstIndex, rest.length), true),
        members
      ),
    };
  },
  [DocumentActionType.UPDATE_TAB_GROUP]: (
    state: DocumentsState,
    payload: {
      tabGroupId: string;
      changes: Omit<TabGroup, "id" | "collapsed">;
    }
  ): DocumentsState => {
    const { tabGroupId, changes } = payload;

    return {
      ...state,
      tabGroups: state.tabGroups.map(tabGroup => {
        return tabGroup.id === tabGroupId
          ? { ...tabGroup, ...changes }
          : tabGroup;
      }),
    };
  },
  [DocumentActionType.REMOVE_TAB_GROUP]: (
    state: DocumentsState,
    payload: string
  ): DocumentsState => {
    return {
      ...state,
      documents: state.documents.map(document => {
        return document.groupId === payload
          ? { ...document, groupId: null }
          : document;
      }),
      tabGroups: state.tabGroups.filter(tabGroup => tabGroup.id !== payload),
    };
  },
  [DocumentActionType.REORDER_TAB_GROUP]: (
    state: DocumentsState,
    payload: {
      tabGroupId: string;
      overDocumentId: string;
    }
  ): DocumentsState => {
    const { tabGroupId, overDocumentId } = payload;

    const run = state.documents.filter(
      document => document.groupId === tabGroupId
    );
    const rest = state.documents.filter(
      document => document.groupId !== tabGroupId
    );

    const overIndex = rest.findIndex(document => document.id === overDocumentId);

    if (!run.length || overIndex === -1) {
      return state;
    }

    const movingForward =
      state.documents.indexOf(run[0]) <
      state.documents.findIndex(document => document.id === overDocumentId);

    return {
      ...state,
      documents: insertAt(
        rest,
        snapOutOfGroup(
          rest,
          movingForward ? overIndex + 1 : overIndex,
          movingForward
        ),
        run
      ),
    };
  },
  [DocumentActionType.SET_DOCUMENT_TAB_GROUP]: (
    state: DocumentsState,
    payload: {
      documentId: string;
      groupId: string | null;
      beforeTabGroupId?: string;
    }
  ): DocumentsState => {
    const { documentId, groupId, beforeTabGroupId } = payload;

    const documentIndex = state.documents.findIndex(
      document => document.id === documentId
    );
    const document = state.documents[documentIndex];

    if (!document || (document.groupId === groupId && !beforeTabGroupId)) {
      return state;
    }

    const rest = state.documents.filter(item => item.id !== documentId);

    const groupStartIndex = beforeTabGroupId
      ? rest.findIndex(item => item.groupId === beforeTabGroupId)
      : -1;

    return {
      ...state,
      documents: insertAt(
        rest,
        groupStartIndex === -1
          ? getGroupEndIndex(rest, groupId ?? document.groupId, documentIndex)
          : groupStartIndex,
        [{ ...document, groupId }]
      ),
      tabGroups: expandGroup(state.tabGroups, groupId),
    };
  },
  [DocumentActionType.TOGGLE_TAB_GROUP_COLLAPSED]: (
    state: DocumentsState,
    payload: string
  ): DocumentsState => {
    const collapsing = state.tabGroups.some(
      tabGroup => tabGroup.id === payload && !tabGroup.collapsed
    );

    const tabGroups = state.tabGroups.map(tabGroup =>
      tabGroup.id === payload
        ? { ...tabGroup, collapsed: !tabGroup.collapsed }
        : tabGroup
    );

    const activeIndex = state.documents.findIndex(
      document =>
        document.id === state.activeDocumentId && document.groupId === payload
    );

    if (!collapsing || activeIndex === -1) {
      return { ...state, tabGroups };
    }

    const nextActiveDocumentId = getNearestVisibleTabId(
      tabGroups,
      state.documents,
      activeIndex
    );

    /*
      With no visible tab left to focus the collapse would hide the active tab and
      normalizeState would expand the group right back, so keep the group as it is.
    */
    if (!nextActiveDocumentId) {
      return state;
    }

    // collapsing over the active tab transfers focus to the nearest visible tab
    return { ...state, tabGroups, activeDocumentId: nextActiveDocumentId };
  },
};

const documentsReducer = (
  state: DocumentsState,
  action: DocumentsAction
): DocumentsState => {
  const { payload, type } = action;

  const handleAction = () => {
    // TODO switch/case remain for type resolution, find the way to do this
    switch (type) {
      case DocumentActionType.SORT_DOCUMENTS:
        return actionHandlers[type](state, payload);

      case DocumentActionType.ADD_DOCUMENT:
        return actionHandlers[type](state, payload);

      case DocumentActionType.REMOVE_DOCUMENT:
        return actionHandlers[type](state, payload);

      case DocumentActionType.REMOVE_ALL_DOCUMENTS:
        return actionHandlers[type](state, payload);

      case DocumentActionType.SET_ACTIVE_DOCUMENT:
        return actionHandlers[type](state, payload);

      case DocumentActionType.CHANGE_DOCUMENT_CONTEXT:
        return actionHandlers[type](state, payload);

      case DocumentActionType.INIT_DOCUMENT_EXECUTION:
        return actionHandlers[type](state, payload);

      case DocumentActionType.UPDATE_DOCUMENT_QUERY_STATEMENT:
        return actionHandlers[type](state, payload);

      case DocumentActionType.CANCEL_DOCUMENT_EXECUTION:
        return actionHandlers[type](state, payload);

      case DocumentActionType.UPDATE_DOCUMENT_EXECUTION_CANCELLATION_STATUS:
        return actionHandlers[type](state, payload);

      case DocumentActionType.SWITCH_DOCUMENT_ACTIVE_QUERY_STATEMENT:
        return actionHandlers[type](state, payload);

      case DocumentActionType.SWITCH_ACTIVE_OUTPUT_TAB:
        return actionHandlers[type](state, payload);

      case DocumentActionType.CHANGE_DOCUMENT_LAYOUT:
        return actionHandlers[type](state, payload);

      case DocumentActionType.UPDATE_DOCUMENT_ENGINE_MONITORING:
        return actionHandlers[type](state, payload);

      case DocumentActionType.REORDER_DOCUMENT:
        return actionHandlers[type](state, payload);

      case DocumentActionType.ADD_DOCUMENT_AI_STATE:
        return actionHandlers[type](state, payload);

      case DocumentActionType.REMOVE_DOCUMENT_AI_STATE:
        return actionHandlers[type](state, payload);

      case DocumentActionType.UPDATE_DOCUMENT_AI_STATE:
        return actionHandlers[type](state, payload);

      case DocumentActionType.SET_QUERY_STATEMENT_STATE:
        return actionHandlers[type](state, payload);

      case DocumentActionType.ADD_TAB_GROUP:
        return actionHandlers[type](state, payload);

      case DocumentActionType.UPDATE_TAB_GROUP:
        return actionHandlers[type](state, payload);

      case DocumentActionType.REMOVE_TAB_GROUP:
        return actionHandlers[type](state, payload);

      case DocumentActionType.REORDER_TAB_GROUP:
        return actionHandlers[type](state, payload);

      case DocumentActionType.SET_DOCUMENT_TAB_GROUP:
        return actionHandlers[type](state, payload);

      case DocumentActionType.TOGGLE_TAB_GROUP_COLLAPSED:
        return actionHandlers[type](state, payload);

      default:
        return state;
    }
  };

  const newState = handleAction();

  return normalizeState(newState);
};
export { documentsReducer };
